import { useEffect, useMemo, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { DollarSign, Star, Users } from "lucide-react";
import ShareCardPanel from "@/components/fanzone/ShareCardPanel";

export default function SalaryCapGM() {
  const [data, setData] = useState(null);
  const [squad, setSquad] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [shareKey, setShareKey] = useState(0);

  const load = () => fz.capgm.pool().then(({ data: d }) => {
    setData(d);
    if (d.my) {
      setSquad(d.my.squad || {});
      setSaved({ rating: d.my.rating, spent: d.my.spent });
    }
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const { spent, rating, complete } = useMemo(() => {
    if (!data) return { spent: 0, rating: 0, complete: false };
    let sp = 0, sum = 0, n = 0;
    data.slots.forEach((slot) => {
      const opt = slot.options.find((o) => o.id === squad[slot.key]);
      if (opt) { sp += opt.price; sum += opt.ovr; n += 1; }
    });
    return { spent: sp, rating: n ? Math.round((sum / n) * 10) / 10 : 0, complete: n === data.slots.length };
  }, [data, squad]);

  if (!data) return <div className="titans-card p-6 text-titans-silver text-sm">Loading the front office…</div>;

  const over = spent > data.budget;
  const pctSpent = Math.min(100, Math.round((spent / data.budget) * 100));

  const submit = async () => {
    if (!complete || over || busy) return;
    setBusy(true);
    try {
      const { data: r } = await fz.capgm.submit(squad);
      toast.success(`Squad rated ${r.rating} OVR — $${r.spent}M spent`);
      if (r.award) toastAward(r.award);
      setSaved({ rating: r.rating, spent: r.spent });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save squad");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="fz-capgm">
      <div className="titans-card p-5 md:p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <DollarSign size={16} className="text-titans-blue" />
          <span className="font-display text-xl uppercase">Salary Cap GM</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-display text-2xl leading-none tabular-nums flex items-center gap-1 justify-end">
              <Star size={14} className="text-amber-500" />
              <span data-testid="fz-capgm-rating">{rating || "—"}</span>
            </div>
            <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-0.5">Team OVR</div>
          </div>
          <button
            onClick={submit}
            disabled={!complete || over || busy}
            data-testid="fz-capgm-submit"
            className="bg-titans-red hover:bg-titans-red-hover text-white px-6 py-3 font-cond uppercase tracking-wider text-xs disabled:opacity-40"
          >
            {saved ? "Update Squad" : "Sign the Squad"}
          </button>
        </div>
      </div>

      <p className="text-titans-silver text-xs font-body mb-3">
        Build the all-time Titans/Oilers 7-man core under a ${data.budget}M cap. Star power costs — balance the books. Hit 85+ OVR for the Dream Team badge.
      </p>

      <div className="mb-6">
        <div className="flex justify-between font-cond uppercase tracking-wider text-[10px] mb-1">
          <span className={over ? "text-titans-red" : "text-titans-silver"} data-testid="fz-capgm-spent">
            ${spent}M spent {over && "— over the cap!"}
          </span>
          <span className="text-titans-silver">${Math.max(0, data.budget - spent)}M remaining</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "#E2E8F0" }}>
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${pctSpent}%`, background: over ? "#C8102E" : "linear-gradient(90deg, #0C2340, #4B92DB)" }}
          />
        </div>
      </div>

      <div className="space-y-5">
        {data.slots.map((slot) => {
          const cons = data.consensus?.[slot.key];
          return (
            <div key={slot.key} data-testid={`fz-capgm-slot-${slot.key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue">{slot.label}</span>
                {saved && cons && (
                  <span className="font-cond uppercase tracking-wider text-[9px] text-titans-silver inline-flex items-center gap-1">
                    <Users size={9} /> Fan favorite: {cons.name} ({cons.pct}%)
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
                {slot.options.map((o) => {
                  const sel = squad[slot.key] === o.id;
                  return (
                    <button
                      key={o.id}
                      onClick={() => setSquad((s) => ({ ...s, [slot.key]: o.id }))}
                      data-testid={`fz-capgm-opt-${slot.key}-${o.id}`}
                      className={`rounded-lg border p-2.5 text-left transition-all duration-150 ${
                        sel ? "bg-titans-navy border-titans-navy text-white scale-[1.02]" : "border-[#E2E8F0] bg-[#F8FAFC] hover:border-titans-blue"
                      }`}
                    >
                      <div className="font-cond uppercase tracking-wide text-xs leading-tight truncate">{o.name}</div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className={`font-display text-sm ${sel ? "text-white/90" : "text-titans-blue"}`}>${o.price}M</span>
                        <span className={`font-cond text-[10px] px-1.5 py-0.5 rounded ${sel ? "bg-white/15" : "bg-[#E2E8F0] text-titans-muted"}`}>
                          {o.ovr} OVR
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 font-cond uppercase tracking-wider text-[10px] text-titans-muted text-center">
        {data.total_gms > 0 ? `${data.total_gms} GM${data.total_gms === 1 ? "" : "s"} have signed a squad` : "Be the first GM to sign a squad"} · 50 XP + 20 bonus for 85+ OVR
      </div>
      </div>

      {saved && (
        <ShareCardPanel
          kind="capgm"
          refreshKey={shareKey}
          text={`My all-time Titans squad rates ${saved.rating} OVR under a $${data.budget}M cap. Beat it. #TitanUp #Titans`}
        />
      )}
    </div>
  );
}
